/**
 * HID 设备通信协议处理模块
 * 包含帧格式定义、校验和计算、命令构造和响应解析
 * 协议格式: AA 53 LENGTH COMMAND DATA... CHECKSUM
 */
import { CommandType, type DeviceStatus, type DeviceDetail, type DeviceColor, type ConnectionType } from './types'
import { parseCallBridgeByte } from './bridge'

// 协议帧头标识
const FRAME_HEADER: [number, number] = [0xAA, 0x53]

const DEVICE_COLOR_MAP: Record<number, DeviceColor> = {
  0x00: 'black',
  0x1F: 'red',
  0x3E: 'orange',
  0x6D: 'yellow',
  0x7C: 'green',
  0x9B: 'blue',
  0xBA: 'indigo',
  0xD9: 'purple',
  0xFF: 'white',
}

/** Convert protocol color bytes (or persisted color names) to one canonical name. */
export function getDeviceColorName(value: unknown): DeviceColor {
  if (typeof value === 'number' && Number.isFinite(value)) return DEVICE_COLOR_MAP[value] || 'unknown'
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return 'unknown'
  if (/^(?:0x)?[0-9a-f]{1,2}$/.test(raw)) {
    const parsed = Number.parseInt(raw.replace(/^0x/, ''), 16)
    if (DEVICE_COLOR_MAP[parsed]) return DEVICE_COLOR_MAP[parsed]
  }
  if (['black', 'blk', 'dark', 'heise'].includes(raw) || raw.includes('黑')) return 'black'
  if (['white', 'write', 'wht', 'light', 'baise'].includes(raw) || raw.includes('白')) return 'white'
  if (['red', 'orange', 'yellow', 'green', 'blue', 'indigo', 'purple'].includes(raw)) return raw as DeviceColor
  return 'unknown'
}

/**
 * 计算协议帧校验和
 * 算法：累加帧头和所有数据字节后，取低 8 位的二进制补码
 */
export function calculateChecksum(data: number[]): string {
  let sum = FRAME_HEADER[0] + FRAME_HEADER[1]

  for (const byte of data) {
    const value = typeof byte === 'string' ? parseInt(byte, 16) : byte
    if (!isNaN(value) && value >= 0 && value <= 0xFF) {
      sum += value
    }
  }

  const sum16bit = sum & 0xFFFF
  const checksum = (~sum16bit + 1) & 0xFF

  return checksum.toString(16).padStart(2, '0').toUpperCase()
}

/**
 * 构建协议命令帧
 * @param command - 命令类型
 * @param data - 命令数据数组
 * @returns 完整的 Uint8Array 帧
 */
export function buildCommand(
  command: CommandType,
  data: number[] = []
): Uint8Array {
  // 对齐旧版: LENGTH 等于整帧字节长度 (AA 53 LENGTH CMD DATA... CHECKSUM)
  const length = data.length + 5 // 帧头(2) + 长度(1) + 命令(1) + 数据(n) + 校验和(1)
  const frameData = [length, command, ...data]
  const checksum = calculateChecksum(frameData)
  const checksumValue = parseInt(checksum, 16)

  return new Uint8Array([...FRAME_HEADER, ...frameData, checksumValue])
}

/**
 * 将十六进制字符串转换为 Uint8Array
 * @param hexString - 格式: "AA 53 08 80 00 01 00 7A" 或连续 HEX 字符串
 * @returns 转换后的字节数组
 */
export function hexStringToBytes(hexString: string): Uint8Array {
  const compact = hexString.replace(/\s+/g, '')
  if (!/^(?:[0-9a-f]{2})+$/i.test(compact)) {
    throw new Error('Invalid HEX string: expected complete hexadecimal byte pairs')
  }
  return new Uint8Array(compact.match(/.{2}/g)!.map(hex => Number.parseInt(hex, 16)))
}

/**
 * 将字节数组转换为十六进制字符串
 * @param bytes - 字节数组
 * @returns 十六进制字符串（大写字母，空格分隔）
 */
export function bytesToHexString(bytes: Uint8Array | number[]): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ')
}

/** 按 LENGTH 验证真实帧，允许 HID 报告尾部填充，不能把填充当成校验和。 */
function readResponsePayload(hexString: string, expectedCommand: number, minPayloadLength: number): Uint8Array | null {
  let bytes: Uint8Array
  try {
    bytes = hexStringToBytes(hexString)
  } catch (error) {
    console.warn('响应 HEX 格式无效:', error)
    return null
  }
  if (bytes.length < 7 || bytes[0] !== FRAME_HEADER[0] || bytes[1] !== FRAME_HEADER[1]) {
    console.warn('响应帧头或长度无效')
    return null
  }
  const totalLength = bytes[2]
  if (bytes[3] !== expectedCommand || totalLength < minPayloadLength + 7 || totalLength > bytes.length) {
    console.warn('响应命令或 LENGTH 无效:', bytes[3], totalLength, bytes.length)
    return null
  }
  const frame = bytes.slice(0, totalLength)
  const receivedChecksum = frame[totalLength - 1]
  const calculatedChecksum = Number.parseInt(calculateChecksum(Array.from(frame.slice(2, -1))), 16)
  if (receivedChecksum !== calculatedChecksum) {
    console.warn('校验和不匹配:', receivedChecksum, calculatedChecksum)
    return null
  }
  // 最后一字节是校验和，不能在短包中被误当成 B 数据。
  return frame.slice(6, -1)
}

/** 小端 packed version：高 4 位 major，中 5 位 minor，低 7 位 patch。 */
function parsePackedVersion(lowByte: number, highByte: number): string {
  if (lowByte == null || highByte == null || (lowByte === 0 && highByte === 0)) return ''
  const word = (highByte << 8) | lowByte
  return `V${(word >> 12) & 0x0F}.${(word >> 7) & 0x1F}.${word & 0x7F}`
}

/** 统一当前链路的兼容字段；USB / dongle 各自的原始字段始终保留。 */
export function selectActiveDeviceStatus(status: DeviceStatus, connectionType: ConnectionType): DeviceStatus {
  const dongle = connectionType === '2.4G'
  status.connectionType = connectionType
  status.isDongleConnection = dongle
  status.micByte = (dongle ? status.dongleMicByte : status.usbMicByte) ?? status.micByte
  status.micMute = (dongle ? status.dongleMicMute : status.usbMicMute) ?? status.micMute
  status.micVolumeLevel = (dongle ? status.dongleMicVolumeLevel : status.usbMicVolumeLevel) ?? status.micVolumeLevel
  status.speakerByte = (dongle ? status.dongleSpeakerByte : status.usbSpeakerByte) ?? status.speakerByte
  status.speakerMute = (dongle ? status.dongleSpeakerMute : status.usbSpeakerMute) ?? status.speakerMute
  status.speakerVolumeLevel = (dongle ? status.dongleSpeakerVolumeLevel : status.usbSpeakerVolumeLevel) ?? status.speakerVolumeLevel
  status.chickenByte = (dongle ? status.dongleChickenByte : status.usbChickenByte) ?? status.chickenByte
  status.chickenOneKey = (dongle ? status.dongleChickenOneKey : status.usbChickenOneKey) ?? status.chickenOneKey
  status.chickenTag = (dongle ? status.dongleChickenTag : status.usbChickenTag) ?? status.chickenTag
  status.eqControlByte = (dongle ? status.dongleEqControlByte : status.usbEqControlByte) ?? status.eqControlByte
  status.eqEnabled = (dongle ? status.dongleEqEnabled : status.usbEqEnabled) ?? status.eqEnabled
  status.eqMode = (dongle ? status.dongleEqMode : status.usbEqMode) ?? status.eqMode
  status.eqTag = (dongle ? status.dongleEqTag : status.usbEqTag) ?? status.eqTag
  status.eqTagByte = status.eqTag
  status.eqByte = status.eqControlByte
  return status
}

/**
 * 解析设备状态响应
 * 从响应帧中提取设备的各项状态参数
 * @param hexString - 十六进制响应帧字符串
 * @returns DeviceStatus 对象或 null（如果解析失败）
 */
export function parseHeadsetResponse(hexString: string, connectionType: ConnectionType = null): DeviceStatus | null {
  const payload = readResponsePayload(hexString, 0x80, 54)
  if (!payload) return null
  const B = payload.slice(0, 54)
  const parseHighBitValueByte = (raw: number) => ({ raw, enabled: (raw & 0x80) ? 1 : 0, value: raw & 0x7F })
  const usbMic = parseHighBitValueByte(B[1])
  const usbSpeaker = parseHighBitValueByte(B[4])
  const usbChicken = parseHighBitValueByte(B[6])
  const voice = parseHighBitValueByte(B[7])
  const sidetone = parseHighBitValueByte(B[8])
  const noise = parseHighBitValueByte(B[10])
  // “低 7 位模式”对应 bit0..6，开关使用 bit7 (0x80)，与已有协议一致。
  const usbEq = parseHighBitValueByte(B[38])
  const dongleEq = parseHighBitValueByte(B[40])
  const dongleChicken = parseHighBitValueByte(B[41])
  const dongleMic = parseHighBitValueByte(B[42])
  const dongleSpeaker = parseHighBitValueByte(B[43])
  const bluetoothStatusByte = B[18]
  const bluetoothConnected = (bluetoothStatusByte & 0x01) ? 1 : 0
  const audioStreamByte = B[44]
  const hardwareMuteByte = B[45]

  return selectActiveDeviceStatus({
    headsetConnected: B[0],
    micByte: usbMic.raw,
    micVolumeLevel: usbMic.value,
    micMute: usbMic.enabled,
    speakerByte: usbSpeaker.raw,
    speakerVolumeLevel: usbSpeaker.value,
    speakerMute: usbSpeaker.enabled,
    usbMicByte: usbMic.raw,
    usbMicMute: usbMic.enabled,
    usbMicVolumeLevel: usbMic.value,
    dongleMicByte: dongleMic.raw,
    dongleMicMute: dongleMic.enabled,
    dongleMicVolumeLevel: dongleMic.value,
    usbSpeakerByte: usbSpeaker.raw,
    usbSpeakerMute: usbSpeaker.enabled,
    usbSpeakerVolumeLevel: usbSpeaker.value,
    dongleSpeakerByte: dongleSpeaker.raw,
    dongleSpeakerMute: dongleSpeaker.enabled,
    dongleSpeakerVolumeLevel: dongleSpeaker.value,
    mixVolume: B[2],
    splitVolume: B[3],
    adaptiveVolume: B[37],
    ...parseCallBridgeByte(B[5]),
    chickenByte: usbChicken.raw,
    chickenOneKey: usbChicken.enabled,
    chickenTag: usbChicken.value,
    usbChickenByte: usbChicken.raw,
    usbChickenOneKey: usbChicken.enabled,
    usbChickenTag: usbChicken.value,
    dongleChickenByte: dongleChicken.raw,
    dongleChickenOneKey: dongleChicken.enabled,
    dongleChickenTag: dongleChicken.value,
    voiceByte: voice.raw,
    voiceChangeEnabled: voice.enabled,
    voiceChangeMode: voice.value,
    sidetoneByte: sidetone.raw,
    sidetoneEnabled: sidetone.enabled,
    sidetoneVolume: sidetone.value,
    btShareEnabled: B[9] === 1 ? 1 : 0,
    noiseByte: noise.raw,
    noiseReductionEnabled: noise.enabled,
    // 原样保留硬件档位；页面需要的默认 Level 1 在 store/UI 层处理。
    noiseReductionMode: noise.value,
    ledMode: B[11],
    eqMode: usbEq.value,
    eqEnabled: usbEq.enabled,
    eqTag: B[12],
    eqControlByte: usbEq.raw,
    usbEqControlByte: usbEq.raw,
    usbEqEnabled: usbEq.enabled,
    usbEqMode: usbEq.value,
    usbEqTag: B[12],
    dongleEqControlByte: dongleEq.raw,
    dongleEqEnabled: dongleEq.enabled,
    dongleEqMode: dongleEq.value,
    dongleEqTag: B[39],
    linkStatus: B[13],
    batteryLevel: B[14],
    powerOffStatus: B[15],
    headsetColor: B[16],
    headsetColorName: getDeviceColorName(B[16]),
    batteryCharging: B[17],
    bluetoothConnectionMark: bluetoothConnected,
    bluetoothStatusByte,
    bluetoothConnected,
    bluetoothInCall: (bluetoothStatusByte & 0x02) ? 1 : 0,
    bluetoothPlaying: (bluetoothStatusByte & 0x04) ? 1 : 0,
    productType: B[19],
    encryptData: Array.from(B.slice(20, 28)),
    dongleVersion: parsePackedVersion(B[28], B[29]),
    softwareVersion: parsePackedVersion(B[30], B[31]),
    bluetoothVersion: parsePackedVersion(B[32], B[33]),
    noiseCancelVersion: parsePackedVersion(B[34], B[35]),
    audioChannelMode: B[36],
    audioStreamByte,
    dongleAudioPlaying: (audioStreamByte & 0x01) ? 1 : 0,
    headsetAudioPlaying: (audioStreamByte & 0x02) ? 1 : 0,
    hardwareMuteByte,
    hardwareMuted: (hardwareMuteByte & 0x01) ? 1 : 0,
    // B[46] 新增定义为场景值；兼容保留原 B[46..53] 调试字段，不猜测场景枚举。
    scenarioValue: B[46],
    reserved47to53: Array.from(B.slice(47, 54)),
    reserved46to53: Array.from(B.slice(46, 54)),
    rawPayloadBytes: Array.from(B),
  }, connectionType)
}

/**
 * 解析设备详情响应
 * 从响应帧中提取设备名称、版本等信息
 * @param hexString - 十六进制响应帧字符串
 * @returns DeviceDetail 对象或 null（如果解析失败）
 */
export function parseDeviceDetailResponse(hexString: string): DeviceDetail | null {
  const B = readResponsePayload(hexString, 0x82, 28)
  if (!B) return null

  const brandLetters = String.fromCharCode(B[0], B[1], B[2]).replace(/\0/g, '-')
  const brandNumber = String(B[3] || 0).padStart(3, '0')
  const brandCode = `${brandLetters}${brandNumber}`

  const categoryLetters = String.fromCharCode(B[4], B[5], B[6]).replace(/\0/g, '-')
  const categoryNumber = String(B[7] || 0).padStart(3, '0')
  const categoryStr = `${categoryLetters}${categoryNumber}`

  let deviceCategory: DeviceDetail['deviceCategory'] = 'unknown'
  if (categoryLetters.startsWith('MIC')) deviceCategory = 'microPhone'
  else if (categoryLetters.startsWith('HEA') || categoryLetters.startsWith('HPH')) deviceCategory = 'headPhone'

  const modelLetters = String.fromCharCode(B[8], B[9]).replace(/\0/g, '-')
  const modelNumbers = String.fromCharCode(B[10], B[11]).replace(/\0/g, '0')
  const modelCode = `${modelLetters}${modelNumbers}`

  const configByte = B[12] || 0
  const colorValue = B[13] || 0
  const pid = ((B[14] || 0) << 8) | (B[15] || 0)
  const vid = ((B[16] || 0) << 8) | (B[17] || 0)

  return {
    deviceCategory,
    vendorId: vid,
    productId: pid,
    deviceName: modelCode || categoryStr || brandCode,
    deviceSerial: Array.from(B.slice(20, 28)).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(''),
    firmwareVersion: '',
    hardwareVersion: parsePackedVersion(B[18] || 0, B[19] || 0),
    protocolVersion: '',
    deviceTypeCode: bytesToHexString(B.slice(4, 12)).replace(/ /g, ''),
    softwareVersion: '',
    bluetoothVersion: '',
    noiseCancelVersion: '',
    dongleVersion: '',
    color: colorValue,
    colorName: getDeviceColorName(colorValue),
  }
}

/**
 * 构建 EQ 命令数据
 */
export function buildEqCommand(eqValues: number[]): Uint8Array {
  // 将 EQ 增益值（-12 到 +12）映射为设备协议值
  const mappedValues = eqValues.map(gain => {
    // 将 dB 转换为设备专用值
    return Math.round((gain + 12) * 2) // 示例映射
  })

  return buildCommand(CommandType.SET_EQ, mappedValues)
}

/**
 * 构建静音命令
 */
export function buildMuteCommand(muted: boolean): Uint8Array {
  return buildCommand(CommandType.SET_MUTE, [muted ? 0x01 : 0x00])
}

/**
 * 构建麦克风静音命令
 */
export function buildMicMuteCommand(muted: boolean): Uint8Array {
  return buildCommand(CommandType.SET_MIC_MUTE, [muted ? 0x01 : 0x00])
}

/**
 * 构建获取状态命令
 * 与旧项目一致：AA 53 08 80 00 01 00 7A
 */
export function buildGetStatusCommand(): Uint8Array {
  // 固定值，与旧项目完全一致
  return hexStringToBytes('AA 53 08 80 00 01 00 7A')
}

/**
 * 构建设备详情查询命令
 * 与旧项目一致：AA 53 08 82 00 01 00 78
 */
export function buildGetDeviceDetailCommand(): Uint8Array {
  // 固定值，与旧项目完全一致
  return hexStringToBytes('AA 53 08 82 00 01 00 78')
}

// 命令十六进制字符串（需要时可直接使用）
export const COMMANDS = {
  GET_STATUS: 'AA 53 08 80 00 01 00 7A',
  GET_DEVICE_DETAIL: 'AA 53 08 82 00 01 00 78',
} as const
